export const CONTRACT_STATES = Object.freeze({
  NONE: "NONE",
  ACTIVE: "ACTIVE",
  EXPIRING: "EXPIRING",
  EXPIRED: "EXPIRED",
});

export const SMS_STATES = Object.freeze({
  NONE: "NONE",
  OK: "OK",
  LOW: "LOW",
  EMPTY: "EMPTY",
});

export const ALERT_TYPES = Object.freeze({
  CONTRACT_EXPIRED: "CONTRACT_EXPIRED",
  CONTRACT_EXPIRING: "CONTRACT_EXPIRING",
  SMS_LOW: "SMS_LOW",
});

export const CONTRACT_SECTION_ID = "branch-contract-section";

const numberFormatter = new Intl.NumberFormat("en-US");

export function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return numberFormatter.format(Math.round(Number(value)));
}

export function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `NT$ ${formatNumber(value)}`;
}

/** 後端日期是 YYYY-MM-DD；畫面與原型一致顯示 YYYY/MM/DD */
export function formatDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10).replace(/-/g, "/");
}

/** 合約期間區塊：YYYY / MM / DD（斜線兩側加空格） */
export function formatDateSpaced(value) {
  const compact = formatDate(value);
  if (compact === "—") return compact;
  return compact.replace(/\//g, " / ");
}

/** 解析 YYYY-MM-DD 為本地日曆日（正午，避免 DST 邊界） */
export function parseLocalDate(value) {
  const match = String(value || "").match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
}

/** 加整曆年；2/29 遇到非閏年落到該年 2/28 */
function addCalendarYears(date, years) {
  const y = date.getFullYear() + years;
  const m = date.getMonth();
  const d = date.getDate();
  const next = new Date(y, m, d, 12, 0, 0, 0);
  // 2/29 → 非閏年會溢成 3/1，改回 2/28
  if (m === 1 && d === 29 && next.getMonth() !== 1) {
    return new Date(y, 1, 28, 12, 0, 0, 0);
  }
  return next;
}

/**
 * 依真實日曆把「今天 → 到期日」拆成約 y 年、zzz 天（含閏年）。
 * fromDate 可用後端／情境的 today；未傳則用本機今天。
 */
export function splitApproxYearsDaysByCalendar(endDateValue, fromDateValue) {
  const end = parseLocalDate(endDateValue);
  const from = fromDateValue ? parseLocalDate(fromDateValue) : (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  })();
  if (!end || !from || end.getTime() <= from.getTime()) {
    return { years: 0, remDays: 0 };
  }

  let years = 0;
  let cursor = from;
  while (true) {
    const next = addCalendarYears(cursor, 1);
    if (next.getTime() > end.getTime()) break;
    years += 1;
    cursor = next;
  }

  const remDays = Math.round((end.getTime() - cursor.getTime()) / 86400000);
  return { years, remDays: Math.max(0, remDays) };
}

/** 解析 `YYYY-MM-DD HH:mm:ss`／ISO 字串 */
export function parseDateTimeParts(value) {
  if (!value) return null;
  const match = String(value).match(
    /(\d{4})[-/](\d{2})[-/](\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!match) return null;
  return {
    y: match[1],
    mo: match[2],
    d: match[3],
    h: match[4] || "00",
    mi: match[5] || "00",
    s: match[6] || "00",
  };
}

/** 發送時間顯示：2026 / 10 / 13  19:47:10 */
export function formatDateTimeSpaced(value) {
  const parts = parseDateTimeParts(value);
  if (!parts) return value ? String(value) : "—";
  return `${parts.y} / ${parts.mo} / ${parts.d}  ${parts.h}:${parts.mi}:${parts.s}`;
}

/**
 * 簡訊匯出確認區間文案（依第一筆～最後一筆）：
 * - 不同天：2026 / 10 / 13  至 2026 / 10 / 14
 * - 同一天：2026 / 10 / 13  15:47 至 2026 / 10 / 13  16:47
 * - 同一分：含秒
 */
export function formatSmsExportRangeLabel(items) {
  const list = (items || [])
    .map((row) => parseDateTimeParts(row?.sentAt))
    .filter(Boolean)
    .sort((a, b) =>
      `${a.y}${a.mo}${a.d}${a.h}${a.mi}${a.s}`.localeCompare(
        `${b.y}${b.mo}${b.d}${b.h}${b.mi}${b.s}`
      )
    );
  if (list.length === 0) return "";
  const first = list[0];
  const last = list[list.length - 1];
  const dateOf = (p) => `${p.y} / ${p.mo} / ${p.d}`;
  const sameDay = first.y === last.y && first.mo === last.mo && first.d === last.d;
  if (!sameDay) {
    return `${dateOf(first)}  至 ${dateOf(last)}`;
  }
  const sameMinute = first.h === last.h && first.mi === last.mi;
  if (sameMinute) {
    return `${dateOf(first)}  ${first.h}:${first.mi}:${first.s} 至 ${dateOf(last)}  ${last.h}:${last.mi}:${last.s}`;
  }
  return `${dateOf(first)}  ${first.h}:${first.mi} 至 ${dateOf(last)}  ${last.h}:${last.mi}`;
}

/** 費用匯出確認：取有日期的最早～最晚 */
export function formatFeeExportRangeLabel(fees) {
  const dates = (fees || [])
    .map((row) => (row?.feeDate ? formatDateSpaced(row.feeDate) : null))
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return "";
  const start = dates[0];
  const end = dates[dates.length - 1];
  return start === end ? start : `${start} 至 ${end}`;
}

export function formatBonusPeriod(contract) {
  const parts = [];
  if (contract?.bonusMonths > 0) parts.push(`${contract.bonusMonths} 個月`);
  if (contract?.bonusDays > 0) parts.push(`${contract.bonusDays} 天`);
  return parts.join(" ");
}

export function contractStatusText(contractStatus) {
  switch (contractStatus?.state) {
    case CONTRACT_STATES.ACTIVE:
      return { tone: "ok", label: "合約有效中" };
    case CONTRACT_STATES.EXPIRING:
      return { tone: "warn", label: "合約即將到期" };
    case CONTRACT_STATES.EXPIRED:
      return { tone: "danger", label: "合約已到期" };
    default:
      return { tone: "muted", label: "尚未建立合約" };
  }
}

/** 一則提醒的文字；同時成立時順序由後端決定（已到期 → 即將到期 → 點數不足） */
export function alertText(alert) {
  switch (alert?.type) {
    case ALERT_TYPES.CONTRACT_EXPIRED:
      return "合約已到期";
    case ALERT_TYPES.CONTRACT_EXPIRING:
      return `合約將於 ${formatNumber(alert.daysRemaining)} 天後到期`;
    case ALERT_TYPES.SMS_LOW:
      return alert.balance > 0 ? `簡訊點數剩 ${formatNumber(alert.balance)} 點` : "簡訊點數已用完";
    default:
      return "";
  }
}

export function isSmsWarning(smsState) {
  return smsState === SMS_STATES.LOW || smsState === SMS_STATES.EMPTY;
}

export function isContractWarning(contractState) {
  return contractState === CONTRACT_STATES.EXPIRING || contractState === CONTRACT_STATES.EXPIRED;
}

export function prefersReducedMotion() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** blob 回應存成檔案；檔名取自 Content-Disposition（filename*=UTF-8''...） */
export function saveBlobResponse(response, fallbackName) {
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
